/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1789948800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Sep 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "taboola",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Taboola",
          "depth": 0,
          "id": "connect-to-taboola"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing audiences",
          "depth": 1,
          "id": "syncing-audiences"
        }, {
          "value": "Syncing conversions",
          "depth": 1,
          "id": "syncing-conversions"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {MatchBoosterDestination, ArrayFanOutTip, MatchRatesExplanation, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!ArrayFanOutTip) _missingMdxReference("ArrayFanOutTip", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!MatchRatesExplanation) _missingMdxReference("MatchRatesExplanation", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-audiences",
                children: _jsx(_components.strong, {
                  children: "Audiences"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any model to Taboola first-party audiences"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.taboola.com/backstage-api/reference/add-or-remove-users",
                children: "Audiences docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-conversions",
                children: _jsx(_components.strong, {
                  children: "Conversions"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any model to Taboola conversions"
            }), _jsx(_components.td, {
              children: "Add"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://help.taboola.com/hc/en-us/article_attachments/7080790629271",
                children: "Conversion docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-taboola",
        children: "Connect to Taboola"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Taboola"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Taboola"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Account ID"
          }), ": Taboola advertiser ID"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Numeric Account ID"
          }), ": Unique identifier for your Taboola account"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client ID"
          }), ": Taboola Backstage API Client ID"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client Secret"
          }), ": Taboola Backstage API Client Secret"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Taboola provides the account ID, the client ID, and client secret to you during the ", _jsx(_components.a, {
          href: "https://developers.taboola.com/backstage-api/reference/welcome",
          children: "API on-boarding"
        }), " process.\nTo get access to the Taboola Backstage API, reach out to Taboola's customer success team.\nYou can find your Taboola numeric account ID in the URL when signed in to Taboola. Example: ", _jsx(_components.code, {
          children: "https://ads.taboola.com/campaigns?locale=en&accountId=<numeric_account_id>"
        })]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Taboola destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Taboola destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-audiences",
        children: "Syncing audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any model to Taboola ", _jsx(_components.a, {
          href: "https://developers.taboola.com/backstage-api/reference/onboarding-overview",
          children: "first-party audiences"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "select-an-existing-audience-or-create-a-new-one",
        children: "Select an existing audience or create a new one"
      }), "\n", _jsx(_components.p, {
        children: "You can create a new audience or sync to an existing one. When creating a new audience, you can optionally enter a name. If you leave it blank, Hightouch uses the name of the model. To use an existing audience, select it from the dropdown."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch creates the audience in Taboola during the first sync run."
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an audience, select model columns and the corresponding Taboola fields.\nYou can match on any of the following Taboola fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Device ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Taboola ID"
          })
        }), "\n"]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Taboola"
      }), "\n", _jsx(ArrayFanOutTip, {
        destinationName: "Taboola"
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch automatically hashes the ", _jsx(_components.strong, {
          children: "Email"
        }), " fields before sending requests to Taboola."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can disable this behavior in the sync configuration. If disabled, the data from the model should be appropriately normalized and hashed according to ", _jsx(_components.a, {
          href: "https://developers.taboola.com/backstage-api/reference/user-id-object",
          children: "Taboola's hashing requirements"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-conversions",
        children: "Syncing conversions"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any model to Taboola ", _jsx(_components.a, {
          href: "https://help.taboola.com/hc/en-us/articles/115006850567-How-to-Track-Conversions-Using-Server-to-Server-Integration-S2S-",
          children: "server-to-server conversions"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync conversion event properties via field mapping."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Taboola, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nTaboola"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsx(_components.p, {
        children: "Below only applies to the audiences sync type."
      }), "\n", _jsx(MatchRatesExplanation, {
        destination: "Taboola"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
